import React, { useState, useEffect } from 'react';
import { Clock, Users, Activity, Calendar, TrendingUp, Brain, Sparkles } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useTheme } from '../../contexts/ThemeContext';
import { AntRangePicker } from '../UI';

const ProjectAnalytics = ({ project }) => {
  const api = useApi();
  const { mode } = useTheme();
  const isDark = mode === 'dark';

  const [memberHoursData, setMemberHoursData] = useState(null);
  const [loading, setLoading] = useState(false);

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultDateRange = () => {
    const today = new Date();
    return { startDate: formatDateForAPI(today), endDate: formatDateForAPI(today) };
  };

  const [selectedDateRange, setSelectedDateRange] = useState(getDefaultDateRange());
  const [selectedQuickRange, setSelectedQuickRange] = useState('today');

  const fetchMemberHours = async (projectId, startDate, endDate) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      const response = await api.get(`/api/v1/projects/${projectId}/member-hours?${params.toString()}`);
      setMemberHoursData(response.data.data);
    } catch (error) {
      console.error('Error fetching member hours:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDateRangeChange = async (startDate, endDate, quickRange = null) => {
    if (startDate && endDate && startDate > endDate) return;
    setSelectedDateRange({ startDate, endDate });
    if (quickRange) setSelectedQuickRange(quickRange);
    else setSelectedQuickRange(null);
    if (project) {
      await fetchMemberHours(project._id || project.projectId || project.id, startDate, endDate);
    }
  };

  const handleDailyAnalysisClick = (userId) => {
    const params = new URLSearchParams({
      memberId: userId,
      date: selectedDateRange.startDate,
      tab: 'daily-analysis'
    });
    window.open(`/dashboard/daily-summary?${params.toString()}`, '_blank');
  };

  const handleHourlyAnalysisClick = (userId) => {
    const params = new URLSearchParams({
      memberId: userId,
      date: selectedDateRange.startDate,
      tab: 'hourly-summaries'
    });
    window.open(`/dashboard/daily-summary?${params.toString()}`, '_blank');
  };

  useEffect(() => {
    if (project) {
      fetchMemberHours(
        project._id || project.projectId || project.id,
        selectedDateRange.startDate,
        selectedDateRange.endDate
      );
    }
  }, [project]);

  const quickRangeBtn = (label, key, getRange) => (
    <button
      type="button"
      onClick={(e) => {
        e.preventDefault();
        const { start, end } = getRange();
        handleDateRangeChange(start, end, key);
      }}
      disabled={loading}
      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-50 ${
        selectedQuickRange === key
          ? isDark
            ? 'bg-white/20 text-white border border-white/30'
            : 'bg-indigo-100 text-indigo-700 border border-indigo-300'
          : isDark
            ? 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
            : 'bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900 border border-slate-200'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header with date controls */}
      <div className={`rounded-xl p-6 border ${
        isDark ? 'bg-white/[0.02] border-white/10' : 'bg-white border-slate-200'
      }`}>
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Title */}
          <div className="flex items-center gap-3 min-w-0">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
              isDark ? 'bg-white/5 border border-white/10' : 'bg-indigo-100 border border-indigo-200'
            }`}>
              <Clock className={`w-5 h-5 ${isDark ? 'text-white' : 'text-indigo-600'}`} />
            </div>
            <div>
              <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Team Hours Analytics
              </h3>
              <p className={`text-sm ${isDark ? 'text-white/40' : 'text-slate-500'}`}>
                {project?.name}
              </p>
            </div>
          </div>

          {/* Date Range Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 w-full lg:w-auto min-w-0 flex-wrap">
            <AntRangePicker
              value={selectedDateRange.startDate && selectedDateRange.endDate ? [
                new Date(selectedDateRange.startDate),
                new Date(selectedDateRange.endDate)
              ] : null}
              onChange={(dates) => {
                if (dates && Array.isArray(dates)) {
                  handleDateRangeChange(
                    dates[0] ? formatDateForAPI(dates[0]) : selectedDateRange.startDate,
                    dates[1] ? formatDateForAPI(dates[1]) : selectedDateRange.endDate
                  );
                }
              }}
              placeholder={['Start date', 'End date']}
              showPresets={false}
              disabled={loading}
              // A hard 460 floor could not shrink, so in any narrow container the
              // picker pushed the quick-range chips past the edge and the title
              // ran into them. Let it flow down to 240 instead.
              style={{ width: '100%', minWidth: 240, maxWidth: 460 }}
            />
            <div className="flex flex-wrap gap-2">
              {quickRangeBtn('Today', 'today', () => {
                const t = formatDateForAPI(new Date());
                return { start: t, end: t };
              })}
              {quickRangeBtn('Yesterday', 'yesterday', () => {
                const y = new Date(); y.setDate(y.getDate() - 1);
                const s = formatDateForAPI(y);
                return { start: s, end: s };
              })}
              {quickRangeBtn('Last 7 Days', 'last7days', () => {
                const e = new Date();
                const s = new Date(); s.setDate(s.getDate() - 6);
                return { start: formatDateForAPI(s), end: formatDateForAPI(e) };
              })}
              {quickRangeBtn('Last 30 Days', 'last30days', () => {
                const e = new Date();
                const s = new Date(); s.setDate(s.getDate() - 29);
                return { start: formatDateForAPI(s), end: formatDateForAPI(e) };
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="relative">
            <div className={`animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 ${isDark ? 'border-white/30' : 'border-slate-300'}`}></div>
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <Clock className={`w-5 h-5 animate-pulse ${isDark ? 'text-white/50' : 'text-slate-400'}`} />
            </div>
          </div>
          <p className={`text-sm mt-4 animate-pulse ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>Loading analytics...</p>
        </div>
      ) : memberHoursData ? (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Total Hours */}
            <div className={`rounded-xl p-5 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-indigo-100'}`}>
                  <Clock className={`w-5 h-5 ${isDark ? 'text-white' : 'text-indigo-600'}`} />
                </div>
              </div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Total Hours</div>
              <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'} ${
                memberHoursData.projectTotals.totalHours === 0 ? 'opacity-40' : ''
              }`}>
                {memberHoursData.projectTotals.totalHours.toFixed(1)}
                <span className={`text-lg ml-1 ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>hrs</span>
              </div>
            </div>

            {/* Active Time */}
            <div className={`rounded-xl p-5 border ${isDark ? 'bg-white/5 border-green-500/20' : 'bg-white border-green-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-green-500/20' : 'bg-green-100'}`}>
                  <Activity className={`w-5 h-5 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                </div>
              </div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Active Time</div>
              <div className={`text-2xl font-bold ${isDark ? 'text-green-400' : 'text-green-600'} ${
                (memberHoursData.projectTotals.totalActiveTimeFormatted || '0h 0m') === '0h 0m' ? 'opacity-40' : ''
              }`}>
                {memberHoursData.projectTotals.totalActiveTimeFormatted || '0h 0m'}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                {memberHoursData.projectTotals.totalActiveTimeMinutes || 0} minutes
              </div>
            </div>

            {/* Idle Time */}
            <div className={`rounded-xl p-5 border ${isDark ? 'bg-white/5 border-orange-500/20' : 'bg-white border-orange-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-orange-500/20' : 'bg-orange-100'}`}>
                  <Clock className={`w-5 h-5 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                </div>
              </div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Idle Time</div>
              <div className={`text-2xl font-bold ${isDark ? 'text-orange-400' : 'text-orange-600'} ${
                (memberHoursData.projectTotals.totalIdleTimeFormatted || '0h 0m') === '0h 0m' ? 'opacity-40' : ''
              }`}>
                {memberHoursData.projectTotals.totalIdleTimeFormatted || '0h 0m'}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                {memberHoursData.projectTotals.totalIdleTimeMinutes || 0} minutes
              </div>
            </div>

            {/* Active Members */}
            <div className={`rounded-xl p-5 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
                  <Users className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-600'}`} />
                </div>
              </div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Active Members</div>
              <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {memberHoursData.projectTotals.activeMembers}
                <span className={`text-lg ml-1 ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>team</span>
              </div>
            </div>

            {/* Total Sessions */}
            <div className={`rounded-xl p-5 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
                  <Activity className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-600'}`} />
                </div>
              </div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Total Sessions</div>
              <div className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {memberHoursData.projectTotals.totalSessions}
                <span className={`text-lg ml-1 ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>logs</span>
              </div>
            </div>

            {/* Date Range */}
            <div className={`rounded-xl p-5 border ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2 rounded-lg ${isDark ? 'bg-white/10' : 'bg-slate-100'}`}>
                  <Calendar className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-600'}`} />
                </div>
              </div>
              <div className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>Date Range</div>
              <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {memberHoursData.dateRange.start}
              </div>
              <div className={`text-xs mt-1 ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>to {memberHoursData.dateRange.end}</div>
            </div>
          </div>

          {/* Member Hours Table */}
          <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-white/5 border-white/10' : 'bg-white border-slate-200'}`}>
            <div className={`px-6 py-4 border-b ${isDark ? 'bg-white/5 border-white/10' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center gap-2">
                <Users className={`w-5 h-5 ${isDark ? 'text-white' : 'text-slate-700'}`} />
                <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>Team Member Hours</h4>
                <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${
                  isDark ? 'bg-white/10 text-gray-300 border-white/10' : 'bg-slate-100 text-slate-600 border-slate-200'
                }`}>
                  {memberHoursData.memberHours.length} members
                </span>
              </div>
            </div>

            {memberHoursData.memberHours.length === 0 ? (
              <div className="text-center py-16">
                <Users className={`w-10 h-10 mx-auto mb-3 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
                <p className={`text-sm font-medium ${isDark ? 'text-white/40' : 'text-slate-400'}`}>
                  No member hours data available
                </p>
                <p className={`text-xs mt-1 ${isDark ? 'text-white/25' : 'text-slate-400'}`}>
                  Team members haven't logged any time for this project yet.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={isDark ? 'bg-white/5' : 'bg-slate-50'}>
                    <tr>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>Member</th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>Position</th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>Total Hours</th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-green-400' : 'text-green-600'}`}>Active Time</th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-orange-400' : 'text-orange-600'}`}>Idle Time</th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>Session Hours</th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>Manual Hours</th>
                      <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>Sessions</th>
                      <th className={`px-6 py-4 text-center text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-400' : 'text-slate-400'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y ${isDark ? 'divide-white/5' : 'divide-slate-100'}`}>
                    {memberHoursData.memberHours.map((member) => (
                      <tr key={member.userId} className={`transition-colors duration-150 ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                              isDark ? 'bg-white/10 border-white/20' : 'bg-slate-100 border-slate-200'
                            }`}>
                              <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-slate-600'}`}>
                                {member.firstName?.[0]}{member.lastName?.[0]}
                              </span>
                            </div>
                            <div>
                              <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                                {member.firstName} {member.lastName}
                              </div>
                              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                {member.email}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2.5 py-1 rounded-lg text-xs font-medium border ${
                            isDark ? 'bg-white/5 border-white/10 text-gray-300' : 'bg-slate-100 border-slate-200 text-slate-600'
                          }`}>
                            {member.position || member.role || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'} ${
                              member.totalHours === 0 ? 'opacity-40' : ''
                            }`}>
                              {member.totalHours.toFixed(1)}h
                            </div>
                            <TrendingUp className={`w-3 h-3 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Activity className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                            <div>
                              <div className={`text-sm font-semibold ${isDark ? 'text-green-400' : 'text-green-600'} ${
                                (member.totalActiveTimeFormatted || '0h 0m') === '0h 0m' ? 'opacity-40' : ''
                              }`}>
                                {member.totalActiveTimeFormatted || '0h 0m'}
                              </div>
                              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                {member.totalActiveTimeMinutes || 0} min
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Clock className={`w-4 h-4 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                            <div>
                              <div className={`text-sm font-semibold ${isDark ? 'text-orange-400' : 'text-orange-600'} ${
                                (member.totalIdleTimeFormatted || '0h 0m') === '0h 0m' ? 'opacity-40' : ''
                              }`}>
                                {member.totalIdleTimeFormatted || '0h 0m'}
                              </div>
                              <div className={`text-xs ${isDark ? 'text-gray-500' : 'text-slate-400'}`}>
                                {member.totalIdleTimeMinutes || 0} min
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                            {member.sessionHours.toFixed(1)}h
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                            {member.manualHours.toFixed(1)}h
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Activity className={`w-3 h-3 ${isDark ? 'text-gray-400' : 'text-slate-400'}`} />
                            <span className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-slate-600'}`}>
                              {member.sessionCount}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center gap-3">
                            <div className="group/tooltip relative flex flex-col items-center">
                              <button
                                type="button"
                                onClick={() => handleHourlyAnalysisClick(member.userId)}
                                className={`p-2 rounded-lg transition-all duration-300 border flex items-center justify-center ${
                                  isDark
                                    ? 'text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20'
                                    : 'text-slate-500 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <Brain className="h-3.5 w-3.5 group-hover/tooltip:scale-110 transition-transform duration-300" />
                              </button>
                              <span className={`text-[10px] mt-1 leading-none ${isDark ? 'text-gray-400' : 'text-slate-400'}`} style={{marginTop:'8px'}}>Hourly</span>
                            </div>
                            <div className="group/tooltip relative flex flex-col items-center">
                              <button
                                type="button"
                                onClick={() => handleDailyAnalysisClick(member.userId)}
                                className={`p-2 rounded-lg transition-all duration-300 border flex items-center justify-center ${
                                  isDark
                                    ? 'text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 border-white/10 hover:border-white/20'
                                    : 'text-slate-500 hover:text-slate-900 bg-slate-50 hover:bg-slate-100 border-slate-200 hover:border-slate-300'
                                }`}
                              >
                                <Sparkles className="h-3.5 w-3.5 group-hover/tooltip:scale-110 transition-transform duration-300" />
                              </button>
                              <span className={`text-[10px] mt-1 leading-none ${isDark ? 'text-gray-400' : 'text-slate-400'}`} style={{marginTop:'8px'}}>Daily</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={`flex flex-col items-center justify-center py-20 rounded-xl border ${
          isDark ? 'bg-white/[0.02] border-white/10' : 'bg-slate-50 border-slate-200'
        }`}>
          <Clock className={`w-10 h-10 mb-3 ${isDark ? 'text-white/20' : 'text-slate-300'}`} />
          <p className={`text-sm font-medium ${isDark ? 'text-white/40' : 'text-slate-400'}`}>Failed to load analytics</p>
          <p className={`text-xs mt-1 ${isDark ? 'text-white/25' : 'text-slate-400'}`}>Please try again later</p>
        </div>
      )}
    </div>
  );
};

export default ProjectAnalytics;
